import { Component, OnInit, ViewChild, Input, OnChanges, AfterViewInit, ChangeDetectionStrategy } from '@angular/core';
import { Location } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { NgForm, UntypedFormBuilder, UntypedFormGroup, Validators, AbstractControl, FormControl } from '@angular/forms';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';

import { Observable, of } from 'rxjs';
import { map, merge, take } from 'rxjs/operators';
import { AlertService, MessageSeverity } from '../../../services/alert.service';
import { AppTranslationService } from '../../../services/app-translation.service';
import { DateFormatterPipe } from '../../../pipes/dateformatter.pipe';
import { fadeInOut } from '../../../services/animations';
import { StringStringTuple, ReportMetadataService, StandardGroupViewModel, StandardGroupService, Int32StringTuple } from '../../index';
import { AuthService } from '../../../services/auth.service';
import { Permission } from '../../../models/permission.model';
import { CanComponentDeactivate } from '../../../services/deactivate-guard';

@Component({
    selector: 'app-standard-group-details',
    templateUrl: './standard-group-details.component.html',
    styleUrls: ['./standard-group-details.component.scss'],
    animations: [fadeInOut],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class StandardGroupDetailsComponent implements OnInit, OnChanges, CanComponentDeactivate  {

    @ViewChild('form', { static: true }) form: NgForm;
    @ViewChild('app-aspect-list', { static: true })

  standardGroup: StandardGroupViewModel;
  users$: Observable<Array<StringStringTuple>>;
  createdByName$: Observable<string>;
  changedByName$: Observable<string>;

  id: number;
  standardGroupForm: UntypedFormGroup;
  public isNew = false;
  private isSaving = false;
  pageTitle: string = "Standardgrupp - Detaljer";

  constructor(
      private alertService: AlertService,
      private translationService: AppTranslationService,
      private standardGroupService: StandardGroupService,
      private reportMetadataService: ReportMetadataService,
      private formBuilder: UntypedFormBuilder,
      private route: ActivatedRoute,
      private router: Router,
      private location: Location,
      private snackBar: MatSnackBar,
      private authService: AuthService
  ) {
      this.id = Number.parseInt(this.route.snapshot.paramMap.get('standardGroupId'));
  }

  ngOnInit(): void {
      this.buildForm();

      this.users$ = this.reportMetadataService.getUserMetadata();

      if (this.id) {
          this.standardGroupService.getStandardGroupById(this.id).subscribe(ok => this.dataLoaded(ok), error => this.alertService.showStickyMessage(error));
      } else {
          this.isNew = true;
          this.standardGroup = this.initializeEmptyObject();
          this.resetForm();
      }
  }

  ngOnChanges(): void {
      if (this.standardGroup) {
          this.isNew = false;
      }
      else {
          this.isNew = true;
          this.standardGroup = this.initializeEmptyObject();
      }

      this.resetForm();
  }


  dataLoaded(standardGroup: StandardGroupViewModel) {
      this.standardGroup = standardGroup;
      this.pageTitle = "Standardgrupp - " + standardGroup.name;

      this.isNew = false;
      this.resetForm();
  }

  public getFormControl(name: string): AbstractControl {
      return this.standardGroupForm.controls[name];
  }


  private buildForm() {
      this.standardGroupForm = this.formBuilder.group({
          standardGroupId: '',
          name: ['', Validators.compose([Validators.required, Validators.maxLength(100)])],
          enabled: '',
          description: '',
          createdBy: '',
          createdDate: '',
          updatedBy: '',
          updatedDate: '',
      });
  }

  public resetForm() {
      if (!this.standardGroup) {
          this.isNew = true;
          this.standardGroup = this.initializeEmptyObject();
      }

      this.standardGroupForm.reset({
          standardGroupId: this.standardGroup.standardGroupId || '',
          name: this.standardGroup.name || '',
          enabled: this.standardGroup.enabled || '',
          description: this.standardGroup.description || '',
          createdBy: this.standardGroup.createdBy || '',
          createdDate: new DateFormatterPipe().transform(this.standardGroup.createdDate, true) || '',
          updatedBy: this.standardGroup.updatedBy || '',
          updatedDate: new DateFormatterPipe().transform(this.standardGroup.updatedDate, true) || '',
      });

      if (this.standardGroup.createdBy) {
          this.createdByName$ = this.reportMetadataService.getUserById(this.standardGroup.createdBy).pipe(map(t => t.item2));
      }
      if (this.standardGroup.updatedBy) {
          this.changedByName$ = this.reportMetadataService.getUserById(this.standardGroup.updatedBy).pipe(map(t => t.item2));
      }
  }

  private initializeEmptyObject(): StandardGroupViewModel {
      var result = <StandardGroupViewModel>{};
      result.enabled = true;

      return result;
  }

  private prepareSave(): StandardGroupViewModel {
      const formModel = this.standardGroupForm.value;

      return {
          standardGroupId: this.isNew ? null : formModel.standardGroupId,
          name: formModel.name,
          enabled: formModel.enabled ? true : false,
          description: formModel.description,
      };
  }

  public save() {
      if (!this.standardGroupForm.valid) {
          this.alertService.showValidationError();
          return;
      }

      this.isSaving = true;
      this.alertService.startLoadingMessage(this.translationService.getTranslation("globalTexts.SavingMessage"));

      const standardGroup = this.prepareSave();

      if (this.isNew) {
          this.standardGroupService.createStandardGroup(standardGroup).subscribe(
              t => this.saveCompleted(t),
              error => this.saveFailed(error));
      }
      else {
          this.standardGroupService.updateStandardGroup(standardGroup.standardGroupId, standardGroup).subscribe(
              t => this.saveCompleted(t),
              error => this.saveFailed(error));
      }
  }

  public cancel() {
      this.location.back();
  }

  // Prevent leaving the form accidentaly if the form is dirty
  canDeactivate(): Observable<boolean> {
      this.alertService.resetStickyMessage();

      if (this.standardGroupForm.dirty) {
          let snackbar = this.snackBar.open(this.translationService.getTranslation("globalTexts.DirtyFormBackQuery"),
              this.translationService.getTranslation("commands.OK"),
              { duration: 5000 });

          let close = snackbar.onAction().pipe(map(() => { return true; }));
          let remain = snackbar.afterDismissed().pipe(map(() => { return false; }));

          return close.pipe(
              merge(remain),
              take(1));

      } else {
          return of(true);
      }
  }

  private saveCompleted(standardGroup: StandardGroupViewModel) {
      this.standardGroup = standardGroup;
      this.id = standardGroup.standardGroupId;
      this.isNew = false;

      this.isSaving = false;
      this.alertService.stopLoadingMessage();
      this.alertService.showMessage(this.translationService.getTranslation("commands.Save"), standardGroup.name + " " + this.translationService.getTranslation("globalTexts.saved"), MessageSeverity.success);
      this.resetForm();
  }

  private saveFailed(error: any) {
      this.isSaving = false;
      this.alertService.stopLoadingMessage();
      this.alertService.showStickyMessage(this.translationService.getTranslation("globalTexts.CouldNotSave"), this.translationService.getTranslation("globalTexts.SaveErrorMessage"), MessageSeverity.error, error);
      this.alertService.showStickyMessage(error, null, MessageSeverity.error);
  }

  hasPermissionToChange(): boolean {
      return this.authService.userHasPermission(Permission.manageModel);
  }
}
